import { defineComponent as _defineComponent } from 'vue'
import {
    ref,
    onMounted,
    watch,
    computed,
    nextTick,
    onBeforeUnmount,
  } from "vue";
  import SwitchComponent from "@/components/ui/SwitchComponent.vue";
  import {
    capitalizeString,
    toKebabCase,
  } from "@/helpers/transformation/string";
  import router from "@/router";
  import plural from "@/features/plural";
  import store from "@/store";
  import {
    heightAnimation,
    removeHeightOfSiblings,
  } from "@/helpers/heightAnimation";
  import { getInitialHoldStatusClass } from "@/helpers/status";
  import { POSITION, useToast } from "vue-toastification";
  import {
    InitialHoldInterface,
    InitialHoldWithLotInterface,
  } from "@/types/InitialHoldInterface";
  import { isInitialHoldDisabled } from "@/helpers/initialHold/isInitialHoldDisabled";
  import { getInitialHoldStatusText } from "@/helpers/initialHold/getInitialHoldStatusText";
  import DropDown from "@/components/ui/dropdown/DropDown.vue";
  import {
    holdStatuses,
    HoldStatusFilterLabel,
    HoldStatusFilterValues,
  } from "@/constants/statusFilters";
  import { formatDate } from "@/helpers/date";
  import InitialHoldStatus from "@/enums/initialHoldStatus";
  import { useRequest } from "@/features/useRequest";
  import { EstateActions } from "@/store/modules/estate";
  import { EstateInterface } from "@/types/EstateInterface";
  
export default /*@__PURE__*/_defineComponent({
  __name: 'initialHoldView',
  setup(__props, { expose: __expose }) {
  __expose();

  const toast = useToast();
  const initialHolds = ref<InitialHoldWithLotInterface[]>([]);
  const activeLotIndex = ref(-1);
  const loading = ref(false);
  const filters = holdStatuses;
  const activeFilter = ref(holdStatuses[0]);
  const tableRefs = ref([]);
  const estate = ref<EstateInterface | null>(null);

  const estateHasCollaborators = computed(() => {
    const collaboratorsLength = estate.value?.collaboratorBusiness?.length;

    return collaboratorsLength && collaboratorsLength > 0;
  });

  const tableHeaders = computed(() => {
    const headers = [
      "Lot No.",
      "Street Name",
      "Name",
      "Email",
      "Phone",
      "Company",
      "Created",
      "Expires",
      "Status",
    ];

    if (estateHasCollaborators.value) {
      headers.push("Business");
    }

    return headers;
  });

  const sortedInitialHolds = computed(() => {
    return getSortedInitialHolds();
  });

  const getSortedInitialHolds = () => {
    if (!initialHolds.value) {
      return initialHolds.value;
    }

    const sortedInitialHolds = initialHolds.value.map((initialHold) => {
      const sortedinitialHold = initialHold;

      sortedinitialHold.initialHold =
        initialHold.initialHold.sort(sortInitialHold);
      return sortedinitialHold;
    });

    return sortedInitialHolds.sort(sortInitialHoldWithLot);
  };

  const sortInitialHoldWithLot = (
    a: InitialHoldWithLotInterface,
    b: InitialHoldWithLotInterface
  ) => {
    return sortInitialHold(a.initialHold[0], b.initialHold[0]);
  };

  const sortInitialHold = (
    a: InitialHoldInterface,
    b: InitialHoldInterface
  ) => {
    return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
  };

  function changeFilter(item: {
    label: HoldStatusFilterLabel;
    value: HoldStatusFilterValues;
  }) {
    activeFilter.value = item;
    getInitialHolds();
  }

  const emptyBlockText = computed(() => {
    return "Hold requests will appear here";
  });
  const getExpirationDate = (date: string) => {
    if (!date) {
      return "-";
    }

    return formatDate(date);
  };

  const getInitialHoldBusiness = (hold: InitialHoldInterface) => {
    return hold.business?.name ? capitalizeString(hold.business.name) : "-";
  };

  const estateId = computed(() => {
    return store.state.user.selectedEstateId;
  });

  const getEstate = async () => {
    const { handleRequestWithLoading } = useRequest(
      undefined,
      undefined,
      POSITION.TOP_LEFT
    );
    const res = await handleRequestWithLoading<EstateInterface, string>(
      EstateActions.GET_ESTATE,
      estateId.value,
      loading
    );

    estate.value = res?.data;
  };

  const getInitialHolds = () => {
    loading.value = true;
    store
      .dispatch("initialHold/getInitialHoldsByStatus", {
        id: estateId.value,
        status: getFilterStatus(),
      })
      .then((res) => {
        initialHolds.value = res.data;
      })
      .catch(() => {
        toast.error(
          "An error occurred while getting the hold requests, try later"
        );
      })
      .finally(() => {
        loading.value = false;
      });
  };

  const getFilterStatus = () => {
    if (Array.isArray(activeFilter.value.value)) {
      return activeFilter.value.value;
    }

    return [activeFilter.value.value];
  };

  const getInitialHoldsCount = (holds: InitialHoldWithLotInterface[]): number =>
    holds.reduce(
      (acc: number, item) =>
        acc + (Array.isArray(item.initialHold) ? item.initialHold.length : 1),
      0
    );

  const initialHoldCounter = computed(() => {
    return getInitialHoldsCount(initialHolds.value);
  });

  const onRow = (id: string) => {
    router.push(`/initial-hold/${id}`);
  };

  const onLotRow = (index: number) => {
    activeLotIndex.value = activeLotIndex.value !== index ? index : -1;
    const section = tableRefs.value[index];

    heightAnimation(section);
    removeHeightOfSiblings(tableRefs.value, activeLotIndex.value);
  };

  const onRezise = () => {
    heightAnimation(tableRefs.value[activeLotIndex.value], "opened", true);
  };

  onMounted(() => {
    if (isInitialHoldDisabled()) {
      router.push("/dashboard");
      return;
    }

    window.addEventListener("resize", onRezise);
    getInitialHolds();
    getEstate();
  });

  onBeforeUnmount(() => {
    window.removeEventListener("resize", onRezise);
  });

const __returned__ = { toast, initialHolds, activeLotIndex, loading, filters, activeFilter, tableRefs, estate, estateHasCollaborators, tableHeaders, sortedInitialHolds, getSortedInitialHolds, sortInitialHoldWithLot, sortInitialHold, changeFilter, emptyBlockText, getExpirationDate, getInitialHoldBusiness, estateId, getEstate, getInitialHolds, getFilterStatus, getInitialHoldsCount, initialHoldCounter, onRow, onLotRow, onRezise, ref, onMounted, watch, computed, nextTick, onBeforeUnmount, SwitchComponent, get capitalizeString() { return capitalizeString }, get toKebabCase() { return toKebabCase }, get router() { return router }, get plural() { return plural }, get store() { return store }, get heightAnimation() { return heightAnimation }, get removeHeightOfSiblings() { return removeHeightOfSiblings }, get getInitialHoldStatusClass() { return getInitialHoldStatusClass }, get POSITION() { return POSITION }, get useToast() { return useToast }, get InitialHoldInterface() { return InitialHoldInterface }, get InitialHoldWithLotInterface() { return InitialHoldWithLotInterface }, get isInitialHoldDisabled() { return isInitialHoldDisabled }, get getInitialHoldStatusText() { return getInitialHoldStatusText }, DropDown, get holdStatuses() { return holdStatuses }, get HoldStatusFilterLabel() { return HoldStatusFilterLabel }, get HoldStatusFilterValues() { return HoldStatusFilterValues }, get formatDate() { return formatDate }, get InitialHoldStatus() { return InitialHoldStatus }, get useRequest() { return useRequest }, get EstateActions() { return EstateActions }, get EstateInterface() { return EstateInterface } }
Object.defineProperty(__returned__, '__isScriptSetup', { enumerable: false, value: true })
return __returned__
}

})